import { t } from 'js/intl/t';
import 'js/i18n/page_translations/work_page_modals/en.wjs';
// The hover tooltip on the "Find Readers" button, for premium members only.
//
// PREMIUM ONLY because it describes what pressing the button is about to do. A free member's press
// goes to the marketing page instead, so promising them a hundred invitations would describe
// something that is not going to happen.
//
// ## Why this is not the design system's Tooltip
//
// design_system/new_tooltip is a React component built on Popover, and two of the three surfaces
// that draw this button could use it — but the profile's button is rendered from Slim
// (Works::FindReadersButtonComponent) inside a card that is fragment-cached, with no React to
// mount and no Rails tooltip component to reach for. One button that looks different depending on
// the page is worse than one that is not built from the DS component, so this is styled to the DS
// tooltip's own tokens instead (see academia_components/find_readers_button.scss).
//
// ## Why it lives on <body>
//
// The analytics Papers table draws the button inside a .panel-body carrying overflow-x: auto for
// narrow screens, and per spec a hidden or auto value on one axis computes the other from visible
// to auto — so that panel clips vertically. A tooltip positioned inside it would be cut off or,
// worse, add scrollbars. Appending to <body> and positioning against the button's viewport rect
// sidesteps every clipping ancestor, which is the same problem floating-ui's portal solves for the
// DS component.
//
// ONE element for the page, moved and re-pointed as the pointer travels, rather than one per
// button: a profile can carry hundreds of cards.

const TOOLTIP_ID = 'find-readers-tooltip';
const TOOLTIP_CLASS = 'find-readers-tooltip';

// Distance from the button's edge to the nubbin's point. Matches the DS tooltip's 4px offset plus
// its 10px nubbin.
const OFFSET = 14;

// Below the button instead of above it when there is not room above — the button sits in a page
// header on the LISWP, which can be near the top of the viewport.
const FLIP_CLASS = 'find-readers-tooltip--below';

export const tooltipText = (): string =>
  t('work_page_modals.find_readers_button.tooltip');

let tooltip: HTMLElement | null = null;
let describedButton: HTMLElement | null = null;

const element = (): HTMLElement => {
  // isConnected, not just non-null: anything that replaces the body wholesale leaves the cached
  // node detached, and a detached tooltip is one that silently never appears again.
  if (tooltip?.isConnected) return tooltip;

  tooltip = document.createElement('div');
  tooltip.id = TOOLTIP_ID;
  tooltip.className = TOOLTIP_CLASS;
  tooltip.setAttribute('role', 'tooltip');
  tooltip.hidden = true;
  tooltip.textContent = tooltipText();
  document.body.appendChild(tooltip);

  return tooltip;
};

export const hideFindReadersTooltip = (): void => {
  if (tooltip != null) tooltip.hidden = true;

  // Removed rather than left pointing at a hidden element, so the button reads the same to a
  // screen reader as it does to everyone else once the pointer has moved on.
  describedButton?.removeAttribute('aria-describedby');
  describedButton = null;
};

export const showFindReadersTooltip = (button: HTMLElement): void => {
  const el = element();
  el.hidden = false;

  describedButton = button;
  button.setAttribute('aria-describedby', TOOLTIP_ID);

  // Measured only once visible: a hidden element has no box to measure against.
  const anchor = button.getBoundingClientRect();
  const own = el.getBoundingClientRect();
  const below = anchor.top - own.height - OFFSET < 0;

  el.classList.toggle(FLIP_CLASS, below);
  el.style.top = `${below ? anchor.bottom + OFFSET : anchor.top - own.height - OFFSET}px`;

  // Centred on the button, then pulled back inside the viewport if that would hang it off an
  // edge — the button sits at the right-hand end of a table row on the analytics Papers tab.
  const centred = anchor.left + anchor.width / 2 - own.width / 2;
  const maxLeft = document.documentElement.clientWidth - own.width - 8;
  el.style.left = `${Math.max(8, Math.min(centred, maxLeft))}px`;
};
